import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export type PhotoKind = 'delivery_slip' | 'fuel_receipt' | 'damage';

export interface TripPhoto {
  id: string;
  tripId: string;
  kind: PhotoKind;
  storagePath: string;
  storageAdapter: string;
  sizeBytes: number;
  width: number;
  height: number;
  mimeType: string;
  exifTakenAt: string | null;
  exifLat: number | null;
  exifLng: number | null;
  uploadedBy: string | null;
  uploadedAt: string;
}

export interface UploadPhotoInput {
  tripId: string;
  kind: PhotoKind;
  file: File | Blob;
  takenAt?: string;
  lat?: number;
  lng?: number;
  onProgress?: (percent: number) => void;
}

export async function listPhotos(tripId: string): Promise<TripPhoto[]> {
  const { data } = await apiClient.get<{ items: TripPhoto[] }>(`/api/v1/trips/${tripId}/photos`);
  return data.items;
}

export async function uploadPhoto(input: UploadPhotoInput): Promise<TripPhoto> {
  const fd = new FormData();
  fd.append('kind', input.kind);
  fd.append('file', input.file);
  if (input.takenAt) fd.append('takenAt', input.takenAt);
  if (typeof input.lat === 'number') fd.append('lat', String(input.lat));
  if (typeof input.lng === 'number') fd.append('lng', String(input.lng));

  const { data } = await apiClient.post<{ photo: TripPhoto }>(
    `/api/v1/trips/${input.tripId}/photos`,
    fd,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (evt.total && input.onProgress) {
          input.onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    }
  );
  return data.photo;
}

export async function deletePhoto(tripId: string, photoId: string): Promise<void> {
  await apiClient.delete(`/api/v1/trips/${tripId}/photos/${photoId}`);
}

export function photoUrl(tripId: string, photoId: string): string {
  // Assumes apiClient base URL has the API origin
  const base = (apiClient.defaults.baseURL || '').replace(/\/$/, '');
  return `${base}/api/v1/trips/${tripId}/photos/${photoId}`;
}

export function useTripPhotos(tripId: string | null | undefined) {
  return useQuery({
    queryKey: ['trip-photos', tripId],
    queryFn: () => listPhotos(tripId!),
    enabled: !!tripId,
  });
}

export function useUploadPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uploadPhoto,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['trip-photos', vars.tripId] });
      qc.invalidateQueries({ queryKey: ['trips'] });
      qc.invalidateQueries({ queryKey: ['trip', vars.tripId] });
    },
  });
}

export function useDeletePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tripId, photoId }: { tripId: string; photoId: string }) => deletePhoto(tripId, photoId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['trip-photos', vars.tripId] });
    },
  });
}
